import { useMemo } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Dropdown } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';

interface Props<T extends FieldValues> {
   control: Control<T>;
   name: FieldPath<T>;
}

export const RfqTimeframeOptions = <T extends FieldValues>({ control, name }: Props<T>) => {
   const translate = useTranslations();

   const timeframeOptions = useMemo(
      () => [
         {
            id: 'ASAP',
            value: translate('page-consumer.request-for-quotation.timeframe.asap')
         },
         {
            id: 'WITHIN_10_DAYS',
            value: translate('page-consumer.request-for-quotation.timeframe.within-10-days')
         },
         {
            id: 'MORE_THAN_10_DAYS',
            value: translate('page-consumer.request-for-quotation.timeframe.more-10-days')
         },
         {
            id: 'NOT_LOOKING_TO_PURCHASE',
            value: translate('page-consumer.request-for-quotation.timeframe.not-looking')
         }
      ],
      [translate]
   );

   const {
      field: { ref, ...field },
      fieldState: { error }
   } = useController({
      name,
      control,
      rules: { required: translate('form-validation.required', { field: translate('form-field.timeframe') }) }
   });

   return (
      <Box gap="md" direction="column">
         <Box direction="row" gap="2xs" items="center">
            <Typography component="span" variant="body-highlight">
               {translate('page-consumer.request-for-quotation.delivery-timeframe')}
            </Typography>
            <Tooltip align="start" pointers text={translate('page-consumer.request-for-quotation.tooltip.delivery-timeframe')}>
               <Box>
                  <Icon icon="info-circle" />
               </Box>
            </Tooltip>
         </Box>
         <Dropdown
            {...field}
            inputRef={ref}
            required
            id="request-for-quotation-delivery-timeframe"
            label={translate('page-consumer.request-for-quotation.select-delivery-timeframe')}
            emptyOption={false}
            options={timeframeOptions}
            error={!!error}
            errorMessage={error?.message}
         />
      </Box>
   );
};
